import { useState } from 'react';
import { toast } from 'react-toastify';
import useUser from '../../../hooks/global/useUser';
import { LoadedUser } from '../../../interfaces';
import SettingsSection from '../SettingsSection';
import FormSmallModal, { FormData } from '../../global/FormSmallModal';
import {
  UpdateBusinessPayload,
  updateBusinessSchema,
} from '../../../types/businesses';
import SectionDivider from '../SectionDivider';
import useUpdateBusiness from '../../../hooks/businesses/useUpdateBusiness';

type AfterCareState = Pick<UpdateBusinessPayload, 'tattooAftercareLink'>;

type ReviewState = Pick<UpdateBusinessPayload, 'reviewLink'>;

function ClientMessagesSettings() {
  const [form, setForm] = useState<FormData | null>(null);

  const { user } = useUser<LoadedUser>();
  const { mutateAsync: updateBusiness } = useUpdateBusiness({
    onSuccess: () => {
      setForm(null);
    },
    step: 'BOOKING_MESSAGES',
  });

  if (!user?.business) return null;

  const { business } = user;

  const updateAftercareLink = async (aftercareLink: string) => {
    await updateBusiness({
      tattooAftercareLink: aftercareLink,
    });
  };

  const updateReviewLink = async (reviewLink: string) => {
    await updateBusiness({
      reviewLink,
    });
  };

  const afterCareForm: FormData<AfterCareState> = {
    initialValues: {
      tattooAftercareLink: business.tattooAftercareLink || '',
    },
    validationSchema: updateBusinessSchema,
    title: 'Edit aftercare link',
    inputRows: [
      [
        {
          inputType: 'text',
          label: 'Aftercare link',
          name: 'tattooAftercareLink',
          placeholder: 'https://example.com/aftercare',
        },
      ],
    ],
    onSubmit: async (values: AfterCareState) => {
      await updateAftercareLink(values.tattooAftercareLink || '');
    },
  };

  const reviewForm: FormData<ReviewState> = {
    initialValues: {
      reviewLink: business.reviewLink || '',
    },
    validationSchema: updateBusinessSchema,
    title: 'Edit review link',
    inputRows: [
      [
        {
          inputType: 'text',
          label: 'Review link',
          name: 'reviewLink',
          placeholder: 'https://example.com/review',
        },
      ],
    ],
    onSubmit: async (values: ReviewState) => {
      await updateReviewLink(values.reviewLink || '');
    },
  };

  return (
    <>
      <SettingsSection
        title="Appointment Reminders"
        subtitle="Messages below will automatically be sent to clients before their appointment. These are default messages, but they will get slightly modified if an appointment has not yet been confirmed, or a waiver needs to be completed."
        rows={[
          {
            title: 'SMS reminder - 48 hours before appointment',
            label:
              'This is a reminder for your upcoming appointment with {{artistName}}. Your appointment is scheduled for {{day}}, from {{startTime}} - {{endTime}} {{timezone}}',
            tooltip:
              'This reminder will be sent to clients 48 hours before their scheduled appointment time. This reminder can not be edited at this time.',
          },
          {
            title: 'SMS reminder - 1 hour before appointment',
            label:
              'This is a reminder for your upcoming appointment with {{artistName}}. Your appointment is scheduled for {{day}}, from {{startTime}} - {{endTime}} {{timezone}}',
            tooltip:
              'This reminder will be sent to clients 1 hour before their scheduled appointment time. This reminder can not be edited at this time.',
          },
        ]}
      />
      <SectionDivider />
      <SettingsSection
        title="Post Appointment Messages"
        subtitle="The following messages can be enabled or disabled. Customers will receive an aftercare link immediately after their appointment is checked out, and one hour later they'll receive a link to leave you a review."
        rows={[
          {
            title: 'Aftercare instructions',
            label: `Hi {{customerName}}! Thanks for visiting ${
              business.name || '...'
            }. Check out our tattoo aftercare instructions through the link below. Feel free to reach out if you have any questions. {{aftercareLink}}`,
            action: {
              type: 'switch',
              checked: !!business.tattooAftercareLink,
              onChange: async checked => {
                if (checked) {
                  toast.info(
                    'To enable the aftercare instructions text message, add an aftercare link below.',
                  );
                } else {
                  await updateAftercareLink('');
                }
              },
            },
            tooltip:
              'If enabled, this text message will be sent to clients immediately after an appointment is checked out or marked as completed through Porter',
          },
          {
            title: 'Aftercare link',
            label: business.tattooAftercareLink || 'No link set',
            isLabelUserInput: !!business.tattooAftercareLink,
            action: {
              type: 'button',
              text: 'Edit',
              onClick: () => setForm(afterCareForm),
            },
          },
          {
            title: 'Request a review',
            label: `👋 {{customerName}}! This is {{artistName}} from ${
              business.name || '...'
            }. I hope you love your new tattoo! Can you please take a moment to leave me a review? It helps so much, thank you! {{reviewLink}}`,
            action: {
              type: 'switch',
              checked: !!business.reviewLink,
              onChange: async checked => {
                if (checked) {
                  toast.info(
                    'To enable the request a review text message, add a Google review link below.',
                  );
                } else {
                  await updateReviewLink('');
                }
              },
            },
          },
          {
            title: 'Google review link',
            label: business.reviewLink || 'No link set',
            isLabelUserInput: !!business.reviewLink,
            action: {
              type: 'button',
              text: 'Edit',
              onClick: () => setForm(reviewForm),
            },
            tooltip:
              "Add your Google review link. Porter screens all reviews, and if a customer is likely to leave you a positive review, we'll direct them to the link you add here to leave you a google review",
          },
        ]}
      />
      <FormSmallModal form={form} onClose={() => setForm(null)} open={!!form} />
    </>
  );
}

export default ClientMessagesSettings;
